import React, { useState, FC, useEffect } from 'react';
import orderBy from 'lodash/orderBy';

import { request } from 'utils/request';
import { Slate } from 'components/Slate';
import { AtomicArtwork } from 'components/artworks/AtomicArtwork';
import { Container } from 'components/UI/Container';

type Score = {
  id: number;
  score: number;
};

const fetchSimilarArtworksScores = async (artworkId: number, per_page: number = 6) => {
  return await request
    .get<Score[]>(`/api/internal/artworks/${artworkId}/scores`, {
      params: {
        type: 'similarity',
        per_page,
      },
    })
    .then(({ data }) => data);
};

type Props = {
  artworkId: number;
  count?: number;
};

export const SimilarArtworksSlate: FC<Props> = ({ artworkId, count = 6 }) => {
  const initialData = Array(count).fill(undefined);
  const [similarArtworkIds, setSimilarArtworkIds] = useState(initialData);

  useEffect(() => {
    if (artworkId) {
      const artworkVisitData = sedition.user.taste.artwork();
      const fetchCount = count * 2; // We'll be removing some items, so fetch more than we need.
      fetchSimilarArtworksScores(artworkId, fetchCount).then((scores) => {
        const modifiedScores = scores.map(({ id, score }) => {
          const visits = artworkVisitData[id];
          // The higher, the more penalty is needed.
          // If we have a lot of similar artworks then
          // no need for decay resistence.
          const decayResistance = scores.length > count ? 0 : 2;
          const visitPenalty = (visits * score) / (visits + decayResistance);
          return {
            id,
            originalScore: score,
            score: visits > 0 ? score - visitPenalty : score,
          };
        });
        const ids = orderBy(modifiedScores, ['score', 'originalScore'], ['desc', 'desc'])
          .slice(0, count)
          .map((score) => score.id);
        setSimilarArtworkIds(ids);
      });
    }
  }, [artworkId]);

  return (
    <Container className='mb-3'>
      <Slate
        items={similarArtworkIds}
        renderItem={(artworkId, index) => (
          <AtomicArtwork artworkId={artworkId} cacheKey='similar-artworks' key={index} />
        )}
        title='Similar works'
        more={{
          title: 'Browse all artworks',
          URL: Routes.artworks_path(),
        }}
      />
    </Container>
  );
};
